import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { EventsManager, ReelsEvents } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { TimelineLabels, WinType } from "../../../abstracts/types/enums";
import { IWinLevel } from "../../../abstracts/types/interfaces";
import { addSkip } from "../../../abstracts/utils/JSUtils";
import { Apocalypse } from "../../Apocalypse";
import { getWinLevel, winLevels } from "../../config/ApocalypseConfig";
import { HIDE_FS_ELEMENTS } from "../../events/ApocalypseEvents";
import { ApocalypseReelsView } from "../../modules/Reels/ApocalypseReelsView";
import { ApocalypseFsLeftContainer } from "../../modules/Reels/fs/ApocalypseFsLeftContainer";
import { ApocalypseFsMultiplierContainer } from "../../modules/Reels/fs/ApocalypseFsMultiplierContainer";
import { showNextBackground } from "../../utils/ApocalypseGraphicUtils";
import { totalWinDecorator } from "../decorators/ApocalypseTotalWinDecorators";
import { ApocalypseNormalWinPresentation } from "../normal/ApocalypseNormalWinPresentation";

export class ApocalypseFreespinWinPresentation extends ApocalypseNormalWinPresentation {
    protected reelsView: ApocalypseReelsView;
    protected fsMultiplier: ApocalypseFsMultiplierContainer;
    protected fsLeft: ApocalypseFsLeftContainer;

    constructor(id: string = `ApocalypseFreespinWinPresentation`) {
        super(id);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: ApocalypseReelsView) => {
            this.reelsView = reelsView;

            this.fsMultiplier = this.reelsView.getFreespinContainer().fsMultiplierContainer;
            this.fsLeft = this.reelsView.getFreespinContainer().fsLeftContainer;
        });
    }

    protected override shouldFinishPresentation(): boolean {
        return this.model.gameData.accumulatedRoundWin <= 0 && this.model.data.freespinsLeft > 0;
    }

    public override start(flowStateController: FlowStateController) {
        super.start(flowStateController);
    }

    protected override handleWinnings() {
        this.flowPresentationTimeline = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
            onStart: () => {
                addSkip(this.flowPresentationTimeline, this.controller);
            },
            onComplete: () => {
                const isSpinWinBelowStake = Apocalypse.model.isWinBelowStake(false);
                const isAccumulatedWinBelowStake = Apocalypse.model.isWinBelowStake(true);

                this.addSpecialAnimations(isSpinWinBelowStake, isAccumulatedWinBelowStake);
            },
        });

        if (this.model.gameData.thisSpinWinning > 0) {
            const winLevel = getWinLevel(this.model.gameData.thisSpinWinning);

            const totalMultiplier = this.model.gameData.brokeTheBank
                ? 20000
                : this.model.gameData.totalFSWinning;

            this.flowPresentationTimeline.add(
                this.fsMultiplier.counter.getUpdateCounterTextAnimation(
                    totalMultiplier,
                    false,
                    true,
                    winLevel.animationDuration / 6
                ),
                `>`
            );
        }
        this.flowPresentationTimeline.resume();
    }

    protected override addSpecialAnimations(
        isWinBelowStake: boolean = false,
        isAccumulatedWinBelow: boolean = false
    ) {
        switch (this.model.gameData.nextMode) {
            case AbstractGameMode.NORMAL: {
                this.keypad.setZeroBetSpinCounter(-1);

                let winValue = this.model.gameData.totalSpinWinnings;
                if (this.model.gameData.brokeTheBank) {
                    winValue =
                        this.model.gameData.accumulatedRoundWin >
                        this.model.gameData.totalSpinWinnings
                            ? this.model.gameData.accumulatedRoundWin
                            : this.model.gameData.totalSpinWinnings;
                }
                let winMultiplier: number = winValue / this.model.gameData.playedBetValue;

                const winLevel: IWinLevel = getWinLevel(winMultiplier);

                const winType: WinType = winLevel.winType;

                this.flowPresentationTimeline = gsap.timeline({
                    autoRemoveChildren: true,
                    smoothChildTiming: true,
                    onComplete: () => {},
                });

                this.keypad.setWin(-1);

                const getOutroTl = () => {
                    return this.winView.showFreespinOutroScreen(
                        this.model.gameData.accumulatedRoundWin,
                        () => {
                            const tl = gsap.timeline({
                                onStart: () => {
                                    // this.reelsView.gameMode = AbstractGameMode.NORMAL;
                                    if (
                                        this.flowPresentationTimeline &&
                                        this.flowPresentationTimeline.labels[
                                            TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE
                                        ]
                                    ) {
                                        this.flowPresentationTimeline.seek(
                                            this.flowPresentationTimeline.labels[
                                                TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE
                                            ],
                                            false
                                        );
                                    }

                                    showNextBackground();
                                },
                                onComplete: () => {
                                    this.onOutroScreenComplete(isAccumulatedWinBelow);
                                },
                            });

                            tl.add(
                                this.winView.openOutroScreenDoors(
                                    this.model.data.mode === AbstractGameMode.SUPER_FREESPIN ||
                                        this.model.data.mode === AbstractGameMode.FREESPIN_BETTER
                                )
                            );
                        },
                        this.model.data.mode === AbstractGameMode.SUPER_FREESPIN ||
                            this.model.data.mode === AbstractGameMode.FREESPIN_BETTER,
                        winLevel,
                        !isAccumulatedWinBelow,
                        this.controller
                    );
                };

                const defaultOutroTimelinePosition = (outroTimeline: gsap.core.Timeline) => {
                    this.flowPresentationTimeline.add(outroTimeline, 0.1);

                    this.flowPresentationTimeline.call(
                        () => {
                            gsap.delayedCall(0.3, () => {
                                showNextBackground();
                            });
                            this.reelsView.gameMode = AbstractGameMode.NORMAL;

                            const skipToNextLevel = () => {
                                const nextLabel: string = outroTimeline.nextLabel();
                                outroTimeline.seek(outroTimeline.labels[nextLabel], false);
                            };
                            this.controller?.setSkipCallback(skipToNextLevel);
                        },
                        undefined,
                        `0.7`
                    );

                    outroTimeline.call(
                        () => {
                            this.controller?.setSkipCallback(undefined);
                        },
                        undefined,
                        `>`
                    );
                };

                if (winType === WinType.BIG || winType === WinType.MAX_CAP) {
                    totalWinDecorator({
                        winLevels: winLevels,
                        winAmount: winValue,
                        timeline: this.flowPresentationTimeline,
                        winScreen: this.winView.totalWinScreen,
                        betValue: this.model.gameData.playedBetValue,
                        flowStateController: this.controller,
                        winIsBelowStake: isAccumulatedWinBelow,
                        reels: this.ucReels,
                        winSymbolsPos: [],
                        keypadPlugin: this.keypad,
                        betWayWins: this.model.gameData.betWayWins,
                        currentGameMode: this.model.data.mode,
                        onDecoratorComplete: () => {
                            if (winType === WinType.MAX_CAP) {
                                this.onOutroScreenComplete(isAccumulatedWinBelow);
                            }
                        },
                        finishSpineAnimationDuration: 5,
                    });

                    if (!isAccumulatedWinBelow) {
                        this.flowPresentationTimeline.call(
                            () => {
                                // ! event hide fs elements
                                EventsManager.trigger(HIDE_FS_ELEMENTS);
                            },
                            undefined,
                            0
                        );
                    }

                    if (winType === WinType.BIG) {
                        const outroTimeline = getOutroTl();

                        if (!isAccumulatedWinBelow) {
                            this.flowPresentationTimeline.add(
                                outroTimeline,
                                `${TimelineLabels.ON_BIG_WINS_COMPLETE_START}-=0.5`
                            );

                            this.flowPresentationTimeline.call(
                                () => {
                                    if (winType === WinType.BIG) {
                                        this.controller?.setSkipCallback(undefined);
                                    }
                                },
                                undefined,
                                `${TimelineLabels.ON_BIG_WINS_COMPLETE_START}-=0.5`
                            );

                            this.flowPresentationTimeline.call(
                                () => {
                                    showNextBackground();
                                    this.reelsView.gameMode = AbstractGameMode.NORMAL;
                                },
                                undefined,
                                `${TimelineLabels.ON_BIG_WINS_COMPLETE_START}+=0.5`
                            );
                        } else {
                            defaultOutroTimelinePosition(outroTimeline);
                        }
                    } else {
                        this.flowPresentationTimeline.call(
                            () => {
                                EventsManager.trigger(HIDE_FS_ELEMENTS);

                                gsap.delayedCall(0.4, () => {
                                    showNextBackground();
                                });
                                this.reelsView.gameMode = AbstractGameMode.NORMAL;
                            },
                            undefined,
                            TimelineLabels.ON_MAX_CAP_COMPLETE_START
                        );
                    }
                } else if (
                    winType === WinType.TINY ||
                    winType === WinType.SMALL ||
                    winType === WinType.MEDIUM ||
                    winType === WinType.LARGE
                ) {
                    const outroTimeline = getOutroTl();

                    defaultOutroTimelinePosition(outroTimeline);
                }

                break;
            }
            default: {
                const totalIsBelowStake: boolean = !isAccumulatedWinBelow ? false : isWinBelowStake;

                this.keypad.setWin(
                    this.model.gameData.accumulatedRoundWin,
                    !totalIsBelowStake,
                    totalIsBelowStake
                );

                this.finishPresentation();

                break;
            }
        }
    }

    protected onOutroScreenComplete(isWinBelowStake: boolean) {
        this.keypad.setWin(this.model.gameData.accumulatedRoundWin, true, isWinBelowStake);
        this.controller?.setSkipCallback(undefined);
        this.showFastSpinButton();
        this.finishPresentation();
    }
}
